/**
 * The cover letter's Referral Disclosure, mirrored for the Submission package
 * panel's live preview (D6). The server renders the letter from
 * `PhoenixCapital.Core/Common/ReferralDisclosure.cs`; both read
 * `tests/fixtures/referral-disclosure-cases.json` (`referralDisclosure.test.ts`,
 * `ReferralDisclosureParityTests.cs`), so a one-sided edit fails.
 *
 * Fixed wording filled from stored fields — never written by a model.
 */

/** PhoenixLegalName.WithoutSuffix — the firm's name as the disclosure prints it. */
export const PHOENIX_NAME = "Phoenix Capital Solutions";

export const REFERRAL_DISCLOSURE_HEADING = "Referral Disclosure";
export const DEFAULT_REFERRAL_FEE_PERCENT = 2;
export const MAX_REFERRAL_FEE_PERCENT = 10;

export const FEE_MISSING_MESSAGE = "Enter the co-broker fee.";
export const FEE_OUT_OF_RANGE_MESSAGE =
  "The co-broker fee must be more than 0% and no more than 10%.";
export const FEE_TOO_PRECISE_MESSAGE = "The co-broker fee can have at most two decimal places.";

export interface ReferralBrokerNames {
  contact: string | null;
  firm: string | null;
}

function clean(value: string | null | undefined): string | null {
  const trimmed = (value ?? "").trim();
  if (trimmed === "") return null;
  return trimmed.toLowerCase() === "tbd" ? null : trimmed;
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Whole-word, case-insensitive containment ("Al" does not match "Allied"). */
function containsWord(haystack: string, needle: string): boolean {
  return new RegExp(`(?<![\\p{L}\\p{N}])${escapeRegExp(needle)}(?![\\p{L}\\p{N}])`, "iu").test(haystack);
}

const TRAILING_PARENTHETICAL = /^(.*?)\s*\(([^()]*)\)\s*$/su;

/** Ruling Q3's cleanup: DBA over company name; trim; blank or "TBD" is
 *  missing; a trailing parenthetical naming the contact goes; a firm still
 *  naming the contact prints alone. */
export function referralNamesFor(
  name: string | null | undefined,
  companyName: string | null | undefined,
  dba: string | null | undefined,
): ReferralBrokerNames {
  let contact = clean(name);
  let firm = clean(dba) ?? clean(companyName);

  if (firm !== null && contact !== null) {
    const paren = TRAILING_PARENTHETICAL.exec(firm);
    if (paren && containsWord(paren[2], contact)) firm = clean(paren[1]);
  }
  if (firm !== null && contact !== null && containsWord(firm, contact)) contact = null;

  return { contact, firm };
}

export function isPrintable(names: ReferralBrokerNames): boolean {
  return names.contact !== null || names.firm !== null;
}

/** D1's "by …" phrase. */
export function introducedBy(names: ReferralBrokerNames): string | null {
  if (names.contact !== null && names.firm !== null) return `${names.contact} of ${names.firm}`;
  return names.contact ?? names.firm;
}

/** The subject of D1's second sentence. */
export function requester(names: ReferralBrokerNames): string | null {
  return names.contact ?? names.firm;
}

/** "2%", "2.5%", "2.25%" — no trailing zeros, no basis. */
export function formatReferralFee(feePercent: number): string {
  const rounded = Math.round((Math.abs(feePercent) + Number.EPSILON) * 100) / 100;
  const body = rounded.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  return (feePercent < 0 && rounded !== 0 ? "-" : "") + body + "%";
}

/** D6 — the inline validation message, or null when the fee is acceptable. */
export function referralFeeProblem(feePercent: number | null | undefined): string | null {
  if (feePercent === null || feePercent === undefined || Number.isNaN(feePercent)) {
    return FEE_MISSING_MESSAGE;
  }
  if (feePercent <= 0 || feePercent > MAX_REFERRAL_FEE_PERCENT) return FEE_OUT_OF_RANGE_MESSAGE;
  if (Number(feePercent.toFixed(2)) !== feePercent) return FEE_TOO_PRECISE_MESSAGE;
  return null;
}

/** The disclosure body exactly as the cover letter prints it, or null when
 *  the broker has nothing printable or the fee would be refused. */
export function referralDisclosureBody(
  buyerName: string,
  names: ReferralBrokerNames,
  feePercent: number,
): string | null {
  const by = introducedBy(names);
  const who = requester(names);
  if (!buyerName.trim() || by === null || who === null || referralFeeProblem(feePercent) !== null) {
    return null;
  }
  return (
    `${buyerName.trim()} was introduced to ${PHOENIX_NAME} by ${by}. ` +
    `${who} requests a co-broker fee of ${formatReferralFee(feePercent)} in connection ` +
    "with this transaction, subject to agreement with the listing broker. " +
    `${PHOENIX_NAME} receives no portion of this fee or any other referral compensation.`
  );
}
